import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import { NumberColumnSettings } from './NumberColumnSettings';
export const ColumnSettingsModal = ({ isOpen, column, onClose, onSave, onDelete, isDark = true, }) => {
    const [editedName, setEditedName] = useState('');
    const [showNumberSettings, setShowNumberSettings] = useState(false);
    const [numberSettings, setNumberSettings] = useState({ format: 'integer' });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        if (column) {
            setEditedName(column.name);
            if (column.type === 'numbers' && column.numberFormat) {
                setNumberSettings(column.numberFormat);
            }
            setError('');
        }
    }, [column, isOpen]);
    const handleSave = async () => {
        if (!column)
            return;
        if (!editedName.trim()) {
            setError('Column name cannot be empty');
            return;
        }
        setIsSaving(true);
        try {
            const updatedColumn = {
                ...column,
                name: editedName.trim(),
                ...(column.type === 'numbers' && { numberFormat: numberSettings }),
            };
            await onSave(updatedColumn);
            console.log('[Column Settings Saved]', {
                columnId: column.id,
                name: editedName.trim(),
                type: column.type,
                ...(column.type === 'numbers' && { numberFormat: numberSettings }),
            });
            onClose();
        }
        catch (err) {
            setError('Failed to save column settings. Please try again.');
            console.error('Error saving column settings:', err);
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleDelete = async () => {
        if (!column)
            return;
        if (!window.confirm(`Delete column "${column.name}" and all its data?`)) {
            return;
        }
        setIsSaving(true);
        try {
            await onDelete(column.id);
            console.log('[Column Deleted]', { columnId: column.id, name: column.name });
            onClose();
        }
        catch (err) {
            setError('Failed to delete column. Please try again.');
            console.error('Error deleting column:', err);
        }
        finally {
            setIsSaving(false);
        }
    };
    if (!isOpen || !column)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center z-50", onClick: onClose, children: _jsxs("div", { className: `${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 w-full max-w-md shadow-2xl`, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("h2", { className: `text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`, children: "Column Settings" }), _jsx("button", { onClick: onClose, className: `p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`, children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: `block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`, children: "Column Name" }), _jsx("input", { type: "text", value: editedName, onChange: (e) => setEditedName(e.target.value), className: `w-full px-3 py-2 rounded border transition-colors ${isDark
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:border-blue-400'
                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500'}` })] }), _jsxs("div", { className: "mb-6", children: [_jsx("label", { className: `block text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`, children: "Column Type" }), _jsx("div", { className: `px-3 py-2 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`, children: _jsx("span", { className: "text-sm capitalize", children: column.type }) })] }), column.type === 'numbers' && (_jsx("div", { className: "mb-6", children: _jsx("button", { onClick: () => setShowNumberSettings(true), className: `w-full px-4 py-2 rounded font-medium transition-colors ${isDark
                                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                    : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`, children: "Configure Number Format" }) })), error && (_jsx("div", { className: "mb-4 p-3 bg-red-500/20 border border-red-500 rounded text-red-400 text-sm", children: error })), _jsxs("div", { className: "flex gap-3 mb-4", children: [_jsx("button", { onClick: onClose, className: `flex-1 px-4 py-2 rounded font-medium transition-colors ${isDark
                                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                        : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`, children: "Cancel" }), _jsx("button", { onClick: handleSave, disabled: isSaving, className: `flex-1 px-4 py-2 rounded font-medium transition-colors ${isSaving
                                        ? isDark
                                            ? 'bg-blue-900 text-blue-200 cursor-not-allowed'
                                            : 'bg-blue-200 text-blue-600 cursor-not-allowed'
                                        : isDark
                                            ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                            : 'bg-blue-500 hover:bg-blue-600 text-white'}`, children: isSaving ? 'Saving...' : 'Save' })] }), _jsxs("button", { onClick: handleDelete, disabled: isSaving, className: `w-full px-4 py-2 rounded font-medium transition-colors flex items-center justify-center gap-2 ${isSaving
                                ? isDark
                                    ? 'bg-red-900/50 text-red-200/50 cursor-not-allowed'
                                    : 'bg-red-100 text-red-500/50 cursor-not-allowed'
                                : isDark
                                    ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                                    : 'bg-red-100 hover:bg-red-200 text-red-600'}`, children: [_jsx(Trash2, { className: "w-4 h-4" }), "Delete Column"] })] }) }), _jsx(NumberColumnSettings, { isOpen: showNumberSettings, onClose: () => setShowNumberSettings(false), onSave: (settings) => {
                    setNumberSettings(settings);
                    setShowNumberSettings(false);
                }, initialSettings: numberSettings, isDark: isDark })] }));
};
